/**
 * CSV Export Utility
 *
 * Generic utility for exporting data to CSV format with proper escaping and formatting.
 * Follows RFC 4180 CSV standard with UTF-8 BOM for Excel compatibility.
 */

export interface CSVColumn<T = any> {
  /** Display name for the column header */
  header: string;
  /** Function to extract/format the value from the data object */
  accessor: (row: T) => string | number | null | undefined;
}

export interface CSVExportOptions {
  /** Filename for the downloaded file (without extension) */
  filename?: string;
  /** Include header row (default: true) */
  includeHeaders?: boolean;
  /** Add UTF-8 BOM for Excel compatibility (default: true) */
  addBOM?: boolean;
}

/**
 * Escapes a CSV cell value according to RFC 4180 with CSV injection prevention
 * - Prevents formula execution in Excel/LibreOffice by prefixing dangerous characters
 * - Wraps in quotes if contains comma, newline, or quote
 * - Doubles any quotes within the value
 */
function escapeCsvValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);

  // CSV Injection prevention: prefix dangerous characters with single quote
  // This prevents Excel/LibreOffice from interpreting them as formulas
  const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
  if (dangerousChars.some(char => stringValue.startsWith(char))) {
    // Prefix with single quote to prevent formula execution
    return `"'${stringValue.replace(/"/g, '""')}"`;
  }

  // Check if value needs escaping (contains comma, newline, or quote)
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"') || stringValue.includes('\r')) {
    // Escape quotes by doubling them and wrap in quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
}

/**
 * Converts an array of objects to CSV format
 */
export function generateCSV<T>(
  data: T[],
  columns: CSVColumn<T>[],
  options: CSVExportOptions = {}
): string {
  const { includeHeaders = true, addBOM = true } = options;

  const rows: string[] = [];

  // Add BOM for Excel UTF-8 compatibility
  const bom = addBOM ? '\uFEFF' : '';

  // Add header row
  if (includeHeaders) {
    const headerRow = columns.map(col => escapeCsvValue(col.header)).join(',');
    rows.push(headerRow);
  }

  // Add data rows
  for (const item of data) {
    const row = columns.map(col => {
      const value = col.accessor(item);
      return escapeCsvValue(value);
    }).join(',');
    rows.push(row);
  }

  return bom + rows.join('\n');
}

/**
 * Downloads CSV content as a file
 */
export function downloadCSV(csvContent: string, filename: string = 'export.csv'): void {
  // Ensure filename has .csv extension
  if (!filename.endsWith('.csv')) {
    filename = `${filename}.csv`;
  }

  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.setAttribute('download', filename);
  link.style.display = 'none';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * All-in-one function to export data to CSV and trigger download
 */
export function exportToCSV<T>(
  data: T[],
  columns: CSVColumn<T>[],
  options: CSVExportOptions = {}
): void {
  const { filename = 'export' } = options;

  const csvContent = generateCSV(data, columns, options);
  downloadCSV(csvContent, filename);
}
